import {
  Show,
  Record,
  Datagrid,
  TextField as RaTextField,
  FunctionField,
  ListContextProvider,
  useNotify,
  TopToolbar,
  ListButton,
  Button as RaButton,
  ShowActionsProps,
} from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Menu,
  MenuItem,
} from "@material-ui/core";
import {
  CheckCircle,
  Cancel,
  GetApp,
  PictureAsPdf,
  TableChart,
  ArchiveOutlined,
  ListAlt,
} from "@material-ui/icons";
import {
  TenantRegistrationDto,
  MasterEldDeviceUsageApiFactory,
  EldDeviceUsageOutputDtoPagedResultDto,
  ReportFormat,
  ReportApiApiFactory,
  EldDeviceUsageOutputDto,
} from "../../../common/data/service-proxies";
import { useReducer, useEffect, useState, useCallback } from "react";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { useHistory } from "react-router-dom";
import { GenericCompactDateOrTimePicker } from "../../common/form/GenericCompactDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import styles from "./TenantEldDevicesUsage.module.css";
import { addMonths, addDays } from "date-fns";
import resourceNames from "../../../common/data/resourceNames";
import { useSignalR } from "../../../context/SignalRContext";
import { ReportsArchive } from "../../reports/ReportsArchive";
import { REPORT_MACHINE_NAMES } from "../../../constants/ReportMacineNames";

type Action = "SET_DATEFROM" | "SET_DATETO";

interface Props {}

interface ViewProps {
  record?: Record;
  showArchive: boolean;
}

interface IFilter {
  dateFrom: Date;
  dateTo: Date;
}

interface IAction {
  type: Action;
  payload: Date;
}

const reducer = (prev: IFilter, action: IAction) => {
  if (!action) {
    return prev;
  }

  switch (action.type) {
    case "SET_DATEFROM":
      return { ...prev, dateFrom: action.payload };
    case "SET_DATETO":
      return { ...prev, dateTo: action.payload };
  }
  return prev;
};

const TenantEldUsageByEventsWrapper = (props: ViewProps) => {
  return <TenantEldUsageByEventsView {...props} />;
};

interface TenantEldUsageActionsProps extends ShowActionsProps {
  showArchive: boolean;
  onToggleArchive: () => void;
}

export const TenantEldUsageActions = ({
  basePath,
  showArchive,
  onToggleArchive,
}: TenantEldUsageActionsProps) => (
  <TopToolbar>
    <RaButton
      label={showArchive ? "Usage" : "Archive"}
      onClick={onToggleArchive}
    >
      {showArchive ? <ListAlt /> : <ArchiveOutlined />}
    </RaButton>
    <ListButton
      basePath={basePath}
      label="resources.common.labels.backToList"
    />
  </TopToolbar>
);

export const TenantEldUsageByEvents = (props: Props) => {
  const [showArchive, setShowArchive] = useState(false);

  return (
    <Show
      {...props}
      title="View ELD usage by events"
      actions={
        <TenantEldUsageActions
          showArchive={showArchive}
          onToggleArchive={() => setShowArchive((current) => !current)}
        />
      }
      component="div"
    >
      <TenantEldUsageByEventsWrapper showArchive={showArchive} />
    </Show>
  );
};

export const TenantEldUsageByEventsView = (props: ViewProps): JSX.Element => {
  const axios = useAxiosInstance();
  const history = useHistory();
  const today = new Date();
  const previousMonth = addMonths(today, -1);
  const [filter, dispatch] = useReducer(reducer, {
    dateFrom: new Date(
      previousMonth.getFullYear(),
      previousMonth.getMonth(),
      1
    ),
    dateTo: addDays(new Date(today.getFullYear(), today.getMonth(), 1), -1),
  });

  const [data, setData] = useState<EldDeviceUsageOutputDtoPagedResultDto>();
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [sortField, setSortField] = useState<string>("dateFrom");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Download dropdown state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const record = props.record as TenantRegistrationDto;

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const hub = useSignalR();
  const notify = useNotify();

  function formatDateToISOString(date: Date | null) {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const isoString = `${year}-${month}-${day}T00:00:00.000Z`;
    return isoString;
  }

  const handleDownloadReport = (format: ReportFormat) => {
    handleDownloadClose();

    if (!axios) {
      return;
    }

    setDownloadLoading(true);

    ReportApiApiFactory(undefined, axios.defaults.baseURL, axios)
      .reportElddeviceusagereportPost({
        dateFrom: formatDateToISOString(filter.dateFrom),
        dateTo: formatDateToISOString(filter.dateTo),
        tenantId: record.createdTenantId || "",
        reportName: REPORT_MACHINE_NAMES.ELD_DEVICE_USAGE_FROM_EVENTS,
        reportFormat: format,
      })
      .then((response) => {
        notify("resources.reports.labels.reportRequested", "success");
        hub?.connection?.invoke("SubscribeToReportUpdates", response.data);
      })
      .catch((error) => {
        console.error("❌ API ERROR:", error);
        setDownloadLoading(false);
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const handleSort = useCallback((field: string, order?: string) => {
    const newSortOrder = order === "DESC" ? "DESC" : "ASC";
    setSortField(field);
    setSortOrder(newSortOrder);
  }, []);

  const handleRowClick = useCallback(
    (id: string | number, basePath: string, clickedRecord: Record) => {
      if (
        clickedRecord?.vehicleId &&
        clickedRecord?.serialNumber &&
        record?.id
      ) {
        const vehicleName = clickedRecord?.vehicleName || "";
        history.push(
          `/${resourceNames.MASTER_TENANT_REGISTRATIONS}/${
            record.id
          }/eld-usage-by-events-by-days/${clickedRecord.vehicleId}/${
            clickedRecord.serialNumber
          }?vehicleName=${encodeURIComponent(vehicleName)}`
        );
      }
      return "";
    },
    [history, record]
  );

  useEffect(() => {
    if (!axios || !record?.createdTenantId) {
      return;
    }

    setLoading(true);

    const filterRules = [];

    filterRules.push({
      field: "dateFrom",
      operator: "equals",
      value: filter.dateFrom.toISOString(),
    });

    filterRules.push({
      field: "dateTo",
      operator: "equals",
      value: filter.dateTo.toISOString(),
    });

    filterRules.push({
      field: "tenantId",
      operator: "equals",
      value: record.createdTenantId,
    });

    const filterRule = {
      condition: "and",
      filterRules: filterRules,
    };

    MasterEldDeviceUsageApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMasterelddeviceusageGetEldDeviceUsageListPost({
        filterRule,
        skipCount: 0,
        maxResultCount: 1000,
        sorting: `${sortField} ${sortOrder.toLowerCase()}`,
      })
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        console.error("❌ API ERROR:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [axios, filter, record?.createdTenantId, sortField, sortOrder]);

  if (props.showArchive) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: 16 }}>
            {record?.name} - ELD Usage Report Archive
          </Typography>
          {record?.createdTenantId && (
            <ReportsArchive
              embedded
              resource={resourceNames.MASTER_ELD_USAGE_REPORT_ARCHIVE}
              targetTenantName={record.name}
              filter={{
                tenantId: record.createdTenantId,
              }}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div className={styles.header}>
          <Typography variant="h6">
            {record?.name} - ELD Usage by Events
          </Typography>
          <div className={styles.filters}>
            <GenericCompactDateOrTimePicker
              variant="outlined"
              PickerComponent={DatePicker}
              source="dateFrom"
              label="Date from"
              value={filter.dateFrom}
              onChange={(value) =>
                dispatch({ type: "SET_DATEFROM", payload: value })
              }
            />
            <GenericCompactDateOrTimePicker
              variant="outlined"
              PickerComponent={DatePicker}
              source="dateTo"
              label="Date to"
              value={filter.dateTo}
              onChange={(value) =>
                dispatch({ type: "SET_DATETO", payload: value })
              }
            />

            {/* Download Button with Dropdown */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={
                  downloadLoading ? <CircularProgress size={16} /> : <GetApp />
                }
                onClick={handleDownloadClick}
                disabled={
                  downloadLoading || !data?.items || data.items.length === 0
                }
                style={{ marginLeft: "8px" }}
              >
                {downloadLoading ? "Generating..." : "Download"}
              </Button>
              <Menu
                anchorEl={downloadAnchorEl}
                keepMounted
                open={Boolean(downloadAnchorEl)}
                onClose={handleDownloadClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                getContentAnchorEl={null}
              >
                <MenuItem
                  onClick={() => handleDownloadReport(ReportFormat.Pdf)}
                  disabled={downloadLoading}
                >
                  <PictureAsPdf
                    style={{ marginRight: "8px", color: "#f44336" }}
                  />
                  PDF
                </MenuItem>
                <MenuItem
                  onClick={() => handleDownloadReport(ReportFormat.Csv)}
                  disabled={downloadLoading}
                >
                  <TableChart
                    style={{ marginRight: "8px", color: "#4caf50" }}
                  />
                  Excel
                </MenuItem>
              </Menu>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {data?.items && (
          <div className={styles.summary}>
            <div>
              <div>ELD devices used by the customer vehicles</div>
              <div>
                {
                  new Set(
                    data.items
                      .filter((item) => (item.numberOfDays || 0) > 0)
                      .map((item) => item.serialNumber)
                  ).size
                }
              </div>
            </div>
            <div>
              <div>Total ELD devices used by the customer</div>
              <div>
                {new Set(data.items.map((item) => item.serialNumber)).size}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: "16px" }}>
              Loading ELD usage data...
            </Typography>
          </div>
        ) : (
          <div style={{ marginTop: "20px" }}>
            {data?.items && data.items.length > 0 ? (
              <ListContextProvider
                value={{
                  data: data.items.reduce(
                    (
                      acc: { [key: string]: EldDeviceUsageOutputDto },
                      item: EldDeviceUsageOutputDto,
                      index: number
                    ) => {
                      acc[item.id || index] = item;
                      return acc;
                    },
                    {}
                  ),
                  ids: data.items.map(
                    (item: EldDeviceUsageOutputDto, index: number) =>
                      item.id || index
                  ),
                  total: data.items.length,
                  perPage: data.items.length,
                  currentSort: { field: sortField, order: sortOrder },
                  resource: "eld-usage-by-events",
                  basePath: "/eld-usage-by-events",
                  setSort: handleSort,
                }}
              >
                <Datagrid
                  hasBulkActions={false}
                  optimized
                  rowClick={handleRowClick}
                >
                  <RaTextField
                    source="serialNumber"
                    label="ELD Serial Number"
                    sortable
                  />
                  <RaTextField source="vehicleName" label="Vehicle" sortable />
                  <FunctionField
                    source="dateFrom"
                    label="Date From"
                    sortable
                    render={(record?: Record) =>
                      record?.dateFrom
                        ? new Date(record.dateFrom).toLocaleDateString()
                        : "-"
                    }
                  />
                  <FunctionField
                    source="dateTo"
                    label="Date To"
                    sortable
                    render={(record?: Record) =>
                      record?.dateTo
                        ? new Date(record.dateTo).toLocaleDateString()
                        : "-"
                    }
                  />
                  <RaTextField
                    source="numberOfDays"
                    label="Number of Days"
                    sortable
                  />
                  <FunctionField
                    source="used"
                    label="Used"
                    sortable={false}
                    render={(record?: Record) =>
                      (record?.numberOfDays || 0) > 0 ? (
                        <CheckCircle style={{ color: "#4caf50" }} />
                      ) : (
                        <Cancel style={{ color: "#f44336" }} />
                      )
                    }
                  />
                </Datagrid>
              </ListContextProvider>
            ) : (
              <Paper style={{ padding: "40px", textAlign: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  No ELD usage data found for the selected date range.
                </Typography>
              </Paper>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
